/**
 * Report View Types
 *
 * Types specifically for the Report View page functionality including:
 * - Report data state management
 * - Filter state and options
 * - AG-Grid configuration types
 */

import { ReportColDefinition, ReportDataWarning, ReportsConfiguration, ReportS3Metadata } from './reportsConfig.types';

// ============================================================================
// Report Data State Types
// ============================================================================

/**
 * State shape for report data in Redux store
 */
export interface ReportDataState {
  /** The raw data rows fetched from API or S3 */
  data: Record<string, unknown>[];
  /** Loading state for data fetch */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Report unique code this data belongs to */
  reportUniqueCode: string | null;
  /** Timestamp when data was last fetched */
  lastFetchTimestamp: number | null;
  /** Total row count for display */
  totalRowCount: number;
  /** Metadata from S3 (timestamp, last_updated_by) - only present for folder-based reports */
  metadata: ReportS3Metadata | null;
  /**
   * Warnings about partial failures when loading report data.
   * Present when some files failed to load but graceful degradation was enabled.
   */
  warnings: ReportDataWarning[];
}

/**
 * Initial state for report data
 */
export const initialReportDataState: ReportDataState = {
  data: [],
  isLoading: false,
  error: null,
  reportUniqueCode: null,
  lastFetchTimestamp: null,
  totalRowCount: 0,
  metadata: null,
  warnings: [],
};

// ============================================================================
// Filter Types
// ============================================================================

/**
 * Represents a single filter option for MultiSelect
 */
export interface FilterOption {
  label: string;
  value: string;
}

/**
 * Filter configuration derived from column definition
 */
export interface ReportFilterConfig {
  /** Field name from report_col_definition */
  fieldName: string;
  /** Display name for the filter label */
  displayName: string;
  /** Data type for proper filtering logic */
  dataType: string;
  /** Available options (distinct values from data) */
  options: FilterOption[];
}

/**
 * Active filter values - maps field name to selected values
 */
export type ReportFilterValues = Record<string, string[]>;

/**
 * Props for the ReportFilters component
 */
export interface ReportFiltersProps {
  /** Column definitions with is_filter flag */
  columnDefinitions: ReportColDefinition[];
  /** Raw report data to extract distinct values from */
  reportData: Record<string, unknown>[];
  /** Current filter values */
  filterValues: ReportFilterValues;
  /** Callback when filters change */
  onFilterChange: (filterValues: ReportFilterValues) => void;
  /** Whether filters are disabled (e.g., during loading) */
  disabled?: boolean;
}

// ============================================================================
// AG-Grid Types
// ============================================================================

/**
 * Props for the ReportDataGrid component
 */
export interface ReportDataGridProps {
  /** Report configuration containing column definitions */
  reportConfig: ReportsConfiguration;
  /** Raw report data rows */
  rowData: Record<string, unknown>[];
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Optional height override (default: 100%) */
  height?: string | number;
}

/**
 * AG-Grid built-in column types.
 *
 * Note: AG-Grid v34+ does not have a 'text' column type.
 * Valid built-in types are: 'rightAligned', 'numericColumn', 'dateColumn'.
 * Custom types can be defined via gridOptions.columnTypes.
 *
 * We use null to indicate no special column type is needed (default behavior).
 */
export type AgGridColumnType = 'rightAligned' | 'numericColumn' | 'dateColumn' | null;

/**
 * Maps report data_type to AG-Grid column type.
 *
 * Returns null for types that should use AG-Grid's default column behavior.
 * AG-Grid v34 does not have a built-in 'text' type - string columns use default behavior.
 */
export const DATA_TYPE_TO_AG_GRID_TYPE: Record<string, AgGridColumnType> = {
  string: null, // Use default column behavior (no specific type needed)
  number: 'rightAligned',
  boolean: null, // Use default with custom value formatter
  date: 'dateColumn',
  datetime: 'dateColumn',
  currency: 'rightAligned',
};

// ============================================================================
// Fetch Parameters
// ============================================================================

/**
 * Parameters for fetching report data via Redux thunk.
 * Abort is handled via RTK's built-in signal (dispatch(thunk).abort()),
 * not via this params object.
 */
export interface FetchReportDataParams {
  /** The report configuration object */
  reportConfig: ReportsConfiguration;
}

/**
 * Result from fetch report data thunk
 */
export interface FetchReportDataResult {
  reportUniqueCode: string;
  data: Record<string, unknown>[];
  totalRowCount: number;
  /** Metadata from S3 metadata.json file (only present for folder-based reports) */
  metadata?: ReportS3Metadata;
  /**
   * Warnings about partial failures when loading report data.
   * Present when some files failed to load but graceful degradation was enabled.
   */
  warnings?: ReportDataWarning[];
}
